import { useSelector } from 'react-redux';

import { AU, NZ, CA } from '../../../../../../constants/country-codes';
import { BillingPlanIds } from '../../../../../../enums/billing-plan-ids';
import { MarketplaceIds } from '../../../../../../enums/marketplace-ids';
import { SellerFeatures } from '../../../../../../enums/seller-features';
import { FeatureFlagDeclarations } from '../../../../../../feature-flags/feature-flag-declarations';
import { useFeatureFlag } from '../../../../../../feature-flags/hooks/use-feature-flag';
import { useEnableWooCommerceCheckoutRates } from '../../../../../../feature-flags/hooks/use-enable-woocommerce-checkout-rates';
import { useIsSellerFeatureEnabled } from '../../../../../../hooks/use-is-seller-feature-enabled';
import Global from '../../../../../../store/global-selectors';
import { BY_DIMENSIONS_CALC_TYPE, BY_WEIGHT_CALC_TYPE, ALL_CALC_TYPE } from '../constants';

const useEnableDeliveryOptionForSellerLocation = () => {
  return useFeatureFlag(FeatureFlagDeclarations.enableDeliveryOptionsForSellerLocation);
};

const useEnableManualStoreDeliveryOption = () => {
  return useFeatureFlag(FeatureFlagDeclarations.enableManualStoreDeliveryOptions);
};

const useEnableDeliveryOptionsCanadianShopify = () => {
  return useFeatureFlag(FeatureFlagDeclarations.enableDeliveryOptionsCanadianShopify);
};
const useEnableDeliveryOptionsCanadianBigCommerce = () => {
  return useFeatureFlag(FeatureFlagDeclarations.enableDeliveryOptionsCanadianBigCommerce);
};

const useIsAnz = () => {
  const includedCountries = [AU, NZ];
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  return includedCountries.includes(sellerHomeCountry);
};
const useIsCanadian = () => {
  const includedCountries = [CA];
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  return includedCountries.includes(sellerHomeCountry);
};

const supportedAnzBillingPlans = [
  BillingPlanIds.Trial,
  BillingPlanIds.SSAccelerateANZQ12023,
  BillingPlanIds.SSScaleANZQ12023,
  BillingPlanIds.SSHighVolumeANZQ22023,
  BillingPlanIds.Standard2025Q2,
  BillingPlanIds.Premium2025Q2,
];

export const useShouldShowBetaTag = () => {
  const isAnz = useIsAnz();
  const isCanadian = useIsCanadian();
  const { billingPlanId } = useSelector(Global.selectSellerSubscriptionPlanInfo) ?? {};
  return (isAnz && supportedAnzBillingPlans.includes(billingPlanId)) || isCanadian;
};

export const useIsDeliveryOptionsAnz = () => {
  const isAnz = useIsAnz();
  const { billingPlanId } = useSelector(Global.selectSellerSubscriptionPlanInfo) ?? {};
  return isAnz && supportedAnzBillingPlans.includes(billingPlanId);
};

const useIsUnsupportedAnzPlan = () => {
  const isAnz = useIsAnz();
  const { billingPlanId } = useSelector(Global.selectSellerSubscriptionPlanInfo) ?? {};
  return isAnz && !supportedAnzBillingPlans.includes(billingPlanId);
};

export const useCalculationMethodType = () => {
  const showByDimensionsCalcMethod = useIsSellerFeatureEnabled(SellerFeatures.CheckoutRatesByDimensions);
  const showByWeightCalcMethod = useIsSellerFeatureEnabled(SellerFeatures.CheckoutRatesByWeight);

  const showAllButtons = showByDimensionsCalcMethod && showByWeightCalcMethod;
  const showDimCalcButtons = showByDimensionsCalcMethod && !showByWeightCalcMethod;

  if (showAllButtons) {
    return ALL_CALC_TYPE;
  }

  if (showDimCalcButtons) {
    return BY_DIMENSIONS_CALC_TYPE;
  }
  return BY_WEIGHT_CALC_TYPE;
};

export const useShouldEnableDeliveryOptions = (marketplaceId) => {
  const isDeliveryOptionForSellerLocationEnabled = useEnableDeliveryOptionForSellerLocation();
  const isShipstationEnabled = useEnableManualStoreDeliveryOption() && marketplaceId === MarketplaceIds.ShipStation;
  const isMagentoEnabled = marketplaceId === MarketplaceIds.Magento;
  const isWooCommerceCheckoutRatesEnabled = useEnableWooCommerceCheckoutRates();
  const isWooCommerceEnabled = isWooCommerceCheckoutRatesEnabled && marketplaceId === MarketplaceIds.WooCommerce;
  const isWixEnabled = marketplaceId === MarketplaceIds.Wix;
  const isBigCommerceV3Enabled = marketplaceId === MarketplaceIds.BigCommerceV3;
  const isUnsupportedAnzPlan = useIsUnsupportedAnzPlan();

  // Canada needs to have a different release flag than the US and ANZ
  // This is not a great approach, but I don't want to refactor this whole file
  // We need to have a discussion around how we want to handle country / billing plan
  // specific feature flags moving forward.
  const isCanadian = useIsCanadian();
  const isCAShopifyEnabled = useEnableDeliveryOptionsCanadianShopify() && marketplaceId === MarketplaceIds.Shopify;
  const isCABigCommerceV3Enabled = useEnableDeliveryOptionsCanadianBigCommerce() && marketplaceId === MarketplaceIds.BigCommerceV3;
  if (isCanadian) {
    return isCAShopifyEnabled || isCABigCommerceV3Enabled;
  }

  return (
    isDeliveryOptionForSellerLocationEnabled &&
    (isShipstationEnabled ||
      isMagentoEnabled ||
      isWooCommerceEnabled ||
      isWixEnabled ||
      marketplaceId === MarketplaceIds.BigCommerce ||
      marketplaceId === MarketplaceIds.Shopify ||
      isBigCommerceV3Enabled) &&
    !isUnsupportedAnzPlan
  );
};
